import { Link } from 'react-router-dom';
import type { Activity } from '../types/activity';

interface ActivityCardProps {
  activity: Activity;
}

// Format relative time (e.g., "2h ago", "1d ago")
function formatRelativeTime(isoString: string): string {
  const date = new Date(isoString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffMins < 60) {
    return `${diffMins}m ago`;
  } else if (diffHours < 24) {
    return `${diffHours}h ago`;
  } else if (diffDays < 7) {
    return `${diffDays}d ago`;
  } else {
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  }
}

// Get action text based on activity type
function getActionText(activity: Activity): string {
  switch (activity.activity_type) {
    case 'rated':
      return `Rated ${activity.media_title}`;
    case 'added_to_watchlist':
      if (activity.watchlist?.name) {
        return `Added ${activity.media_title} to ${activity.watchlist.name}`;
      }
      return `Added ${activity.media_title} to Watchlist`;
    case 'added_to_favorites':
      return `Added ${activity.media_title} to Favorites`;
    case 'added_to_watched':
      return `Watched ${activity.media_title}`;
    case 'created_list':
      return `Created list "${activity.watchlist?.name || 'Untitled'}"`;
    case 'followed':
      return 'Started following';
    default:
      return '';
  }
}

// User avatar with initials fallback
function UserAvatar({ username, avatar, size = 32 }: { username: string; avatar?: string | null; size?: number }) {
  const initials = username.slice(0, 2).toUpperCase();

  return (
    <div
      className="rounded-full overflow-hidden shrink-0 bg-gradient-to-br from-teal-400 to-orange-500 flex items-center justify-center"
      style={{ width: size, height: size }}
    >
      {avatar ? (
        <img src={avatar} alt={username} className="w-full h-full object-cover" />
      ) : (
        <span className="text-white text-xs font-medium">{initials}</span>
      )}
    </div>
  );
}

export function ActivityCard({ activity }: ActivityCardProps) {
  const hasMedia = activity.media_id && activity.media_type && activity.activity_type !== 'created_list' && activity.activity_type !== 'followed';

  // Get the route for media based on media_type
  const getMediaRoute = () => {
    if (!activity.media_id || !activity.media_type) return '';
    return `/${activity.media_type}/${activity.media_id}`;
  };

  return (
    <div className="bg-[#1A1F26]/30 rounded-xl p-3 hover:bg-[#1A1F26]/50 transition-colors overflow-hidden">
      {/* Header: Avatar + Username + Timestamp */}
      <div className="flex items-center gap-2 mb-2">
        <UserAvatar username={activity.user.username} avatar={activity.user.avatar_url} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <Link
              to={`/u/${activity.user.username}`}
              className="text-sm font-medium text-white hover:text-teal-400 transition-colors"
            >
              @{activity.user.username}
            </Link>
            <span className="text-xs text-slate-500">·</span>
            <span className="text-xs text-slate-500">{formatRelativeTime(activity.created_at)}</span>
          </div>
        </div>
      </div>

      {/* Action Text */}
      <p className="text-sm text-slate-300 mb-2">
        {getActionText(activity)}
        {activity.activity_type === 'rated' && activity.rating && (
          <span className="ml-1 text-yellow-400">⭐ {activity.rating}</span>
        )}
        {activity.activity_type === 'followed' && activity.target_user && (
          <Link
            to={`/u/${activity.target_user.username}`}
            className="ml-1 text-white hover:text-teal-400 transition-colors"
          >
            @{activity.target_user.username}
          </Link>
        )}
      </p>

      {/* Movie/TV Card (if applicable) */}
      {hasMedia && activity.media_title && (
        <Link
          to={getMediaRoute()}
          className="flex gap-3 p-2 -mx-1 rounded-lg hover:bg-slate-700/30 transition-colors"
        >
          {/* Poster */}
          <div className="w-[60px] aspect-[2/3] rounded-lg overflow-hidden bg-slate-700 shrink-0">
            {activity.media_poster ? (
              <img
                src={`https://image.tmdb.org/t/p/w185${activity.media_poster}`}
                alt={activity.media_title}
                className="w-full h-full object-cover"
                loading="lazy"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-slate-500">
                🎬
              </div>
            )}
          </div>
          {/* Movie Info */}
          <div className="flex-1 min-w-0 py-1">
            <h4 className="text-sm font-medium text-white line-clamp-2">
              {activity.media_title}
            </h4>
          </div>
        </Link>
      )}
    </div>
  );
}
